<script setup lang="ts">
import { useIdeaFormStore } from '~/stores/use-idea-form-store';
import { IDEA_SEASONS, ideaTimingDisplay } from '~/utils/idea-timing';

// Rough-timing picker for the Event Idea form.
//
// Ideas are deliberately half-formed, so the precision itself is a first-class
// choice: an exact window, a month, a season, a year, or nothing at all. Only
// `exact` carries datetimes — every coarse kind sends literal nulls, which the
// store's `buildTiming()` enforces via `buildIdeaTiming`.
//
// Binds straight to the shared store (the form is spread across components and
// both the create and edit pages read the same state).

const props = withDefaults(defineProps<{ disabled?: boolean }>(), {
  disabled: false,
});

const store = useIdeaFormStore();

const KIND_OPTIONS = [
  { value: 'exact', label: 'Exact dates' },
  { value: 'month', label: 'A month' },
  { value: 'season', label: 'A season' },
  { value: 'year', label: 'A year' },
  { value: 'tba', label: 'No idea yet' },
] as const;

const MONTH_ITEMS = [
  { title: 'January', value: '1' },
  { title: 'February', value: '2' },
  { title: 'March', value: '3' },
  { title: 'April', value: '4' },
  { title: 'May', value: '5' },
  { title: 'June', value: '6' },
  { title: 'July', value: '7' },
  { title: 'August', value: '8' },
  { title: 'September', value: '9' },
  { title: 'October', value: '10' },
  { title: 'November', value: '11' },
  { title: 'December', value: '12' },
];

const SEASON_ITEMS = IDEA_SEASONS.map((s) => ({ title: s, value: s }));

const startDateRules = [
  (v: unknown) =>
    (typeof v === 'string' && v.trim() !== '') || 'A start date is required',
];

const endDateRules = computed(() => [
  () =>
    !store.endDate ||
    !store.startDate ||
    store.endDate >= store.startDate ||
    'The end date must not be before the start',
]);

const monthRules = [
  (v: unknown) => (typeof v === 'string' && v !== '') || 'Pick a month',
];

const seasonRules = [
  (v: unknown) => (typeof v === 'string' && v !== '') || 'Pick a season',
];

const yearRules = [
  (v: unknown) =>
    (typeof v === 'string' && /^\d{4}$/.test(v.trim())) ||
    'Enter a 4-digit year',
];

// The `display` string the backend will store verbatim, previewed live so the
// author sees exactly what every read surface will render. Empty until the
// chosen kind's fields are complete.
const displayPreview = computed(() =>
  ideaTimingDisplay({
    kind: store.timingKind,
    startDate: store.startDate,
    endDate: store.endDate,
    month: store.month,
    season: store.season,
    year: store.year,
  }),
);

// Any 422 the backend raised against the timing sub-object. `timing.display` is
// also where the store's own completeness check lands, so it doubles as the
// "finish choosing" message.
const timingError = computed(
  () =>
    store.serverErrors['timing.display']?.[0] ??
    store.serverErrors['timing.kind']?.[0] ??
    null,
);
</script>

<template>
  <div>
    <div class="text-subtitle-2 font-weight-medium mb-1">
      When might this happen?
    </div>
    <p class="text-body-2 text-medium-emphasis mb-2">
      Be as vague as you like — an idea doesn't need a date to be worth
      floating.
    </p>

    <v-btn-toggle
      v-model="store.timingKind"
      color="primary"
      variant="outlined"
      divided
      mandatory
      :disabled="props.disabled"
      class="mb-3 flex-wrap"
    >
      <v-btn
        v-for="option in KIND_OPTIONS"
        :key="option.value"
        :value="option.value"
      >
        {{ option.label }}
      </v-btn>
    </v-btn-toggle>

    <template v-if="store.timingKind === 'exact'">
      <v-row>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="store.startDate"
            label="Start date"
            type="date"
            :rules="startDateRules"
            :error-messages="store.serverErrors['timing.starts_at']"
            :disabled="props.disabled"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="store.endDate"
            label="End date (optional)"
            type="date"
            hint="Leave blank for a single day"
            :rules="endDateRules"
            :error-messages="store.serverErrors['timing.ends_at']"
            :disabled="props.disabled"
          />
        </v-col>
      </v-row>
    </template>

    <template v-else-if="store.timingKind === 'month'">
      <v-row>
        <v-col cols="12" sm="6">
          <v-select
            v-model="store.month"
            :items="MONTH_ITEMS"
            label="Month"
            :rules="monthRules"
            :disabled="props.disabled"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="store.year"
            label="Year"
            type="number"
            :rules="yearRules"
            :disabled="props.disabled"
          />
        </v-col>
      </v-row>
    </template>

    <template v-else-if="store.timingKind === 'season'">
      <v-row>
        <v-col cols="12" sm="6">
          <v-select
            v-model="store.season"
            :items="SEASON_ITEMS"
            label="Season"
            :rules="seasonRules"
            :disabled="props.disabled"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="store.year"
            label="Year"
            type="number"
            :rules="yearRules"
            :disabled="props.disabled"
          />
        </v-col>
      </v-row>
    </template>

    <template v-else-if="store.timingKind === 'year'">
      <v-row>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="store.year"
            label="Year"
            type="number"
            :rules="yearRules"
            :disabled="props.disabled"
          />
        </v-col>
      </v-row>
    </template>

    <p v-else class="text-body-2 text-medium-emphasis">
      No timing at all — this idea will show as "Dates TBA" until you pin one
      down.
    </p>

    <div v-if="displayPreview" class="text-body-2 mt-1">
      Shown as
      <span class="font-weight-medium">{{ displayPreview }}</span>
    </div>

    <div v-if="timingError" class="text-error text-caption mt-1">
      {{ timingError }}
    </div>
  </div>
</template>
